import { useMemo, useContext, useState } from "react";
import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import { useTranslation } from "react-i18next";
import { CircularProgress, IconButton, List, Typography } from "@mui/material";
import { useStyles } from "./useSecondaryCodeRequirementsColumns.styles";
import moment from "moment-timezone";
import { EventReportsEntity } from "entities/EventReports/EventReportsEntity";
import { FailureDetailsEntity } from "entities/FailureDetailsEntity/FailureDetailsEntity";
import { UserContext } from "contexts/UserContext/UserContext";
import { DefaultTranslationFieldsInTable } from "constants/constants";
import { MillDashboardContext } from "contexts/MillDashboardsContext/MillDashboardsContext";
import { IEventReportRow } from "../SecondaryCodeRequerimentsInterfaces";
import { LocationsContext } from "contexts/LocationsContext/LocationsContext";
import { translateDefaultEntity } from "utilities/localesUtilities";
import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/Clear";
import CheckIcon from "@mui/icons-material/Check";
import { IFailureCode } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import {
    IEditionSecondaryCodes,
    SecondaryCodesTableColumnsIdEnum,
} from "./useSecondaryCodeRequirementsColumnsInterface";
import {
    FunctionalLocationsDropdown,
    AreasDropdown,
    SecondaryFailureCodeDetailsDropdown,
    FailureCodeDetailsDropdown,
    FailureTypeDetailsDropdown,
} from "../components";

interface ISecondaryCodeRequirementsColumns extends Omit<IMainTableColumn, "render"> {
    render: (row: IEventReportRow) => string | JSX.Element | null | undefined;
}

export const useSecondaryCodeRequirementsColumns = (
    refetch: () => Promise<void>,
): ISecondaryCodeRequirementsColumns[] => {
    const { t, i18n } = useTranslation();
    const { user_id } = useContext(UserContext);
    const [edit, setEdit] = useState<IEditionSecondaryCodes[]>([]);
    const [saving, setSaving] = useState<
        {
            id: number;
        }[]
    >([]);
    const classes = useStyles();
    const { updateEventReportMutation } = EventReportsEntity.UpdateEventReportBO();
    const { failureCodeUnknown } = FailureDetailsEntity.GetUnknownFailureCode();
    const { defaultFailureTypes } = FailureDetailsEntity.GetDefaultFailureTypes();
    const { millTimezone } = useContext(LocationsContext);
    const { showOnlyPending, refreshPendingEventReportsCount } = useContext(MillDashboardContext);

    const userTimezone = useMemo(() => moment.tz.guess(), []);

    const columns = useMemo(
        (): ISecondaryCodeRequirementsColumns[] => [
            {
                id: SecondaryCodesTableColumnsIdEnum.TIME_STOP,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.secondary_code_requirements_table.time_stop").toUpperCase()}
                    </Typography>
                ),

                render: (row) =>
                    moment
                        .utc(row.start_date)
                        .tz(millTimezone ?? userTimezone)
                        .format("MMM D, h:mm A"),
            },
            {
                id: SecondaryCodesTableColumnsIdEnum.TIME_RESTART,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.secondary_code_requirements_table.time_start").toUpperCase()}
                    </Typography>
                ),

                render: (row) =>
                    row.end_date ? (
                        moment
                            .utc(row.end_date)
                            .tz(millTimezone ?? userTimezone)
                            .format("h:mm A")
                    ) : (
                        <Typography variant="subtitle1" className={classes.waiting}>
                            {t("locations.secondary_code_requirements_table.waiting")}
                        </Typography>
                    ),
            },
            {
                id: SecondaryCodesTableColumnsIdEnum.DURATION,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.secondary_code_requirements_table.duration").toUpperCase()}
                    </Typography>
                ),

                render: (row) =>
                    row.duration ? (
                        row.duration
                    ) : (
                        <Typography variant="subtitle1" className={classes.toBeDefined}>
                            {t("locations.secondary_code_requirements_table.to_be_defined")}
                        </Typography>
                    ),
            },
            {
                id: SecondaryCodesTableColumnsIdEnum.MACHINE_CENTER,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.secondary_code_requirements_table.machine_center").toUpperCase()}
                    </Typography>
                ),

                render: (row) => row.machine_center_short_name,
            },
            {
                id: SecondaryCodesTableColumnsIdEnum.FAILURE_TYPE,
                hideSortIcon: !Boolean(edit.findIndex((event) => event.edit)),
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.secondary_code_requirements_table.failure_type").toUpperCase()}
                    </Typography>
                ),

                render: (row) => {
                    const existingEdition = edit.findIndex((event) => event.id === row.event_report_id);

                    return edit[existingEdition]?.edit ? (
                        <FailureTypeDetailsDropdown edit={edit} setEdit={setEdit} row={row} />
                    ) : (
                        translateDefaultEntity(
                            i18n.language,
                            row.failure_type,
                            "name",
                            DefaultTranslationFieldsInTable.FAILURE_TYPE,
                        )
                    );
                },
            },
            {
                id: SecondaryCodesTableColumnsIdEnum.AREA,
                hideSortIcon: !Boolean(edit.findIndex((event) => event.edit)),
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.secondary_code_requirements_table.area").toUpperCase()}
                    </Typography>
                ),

                render: (row) => {
                    const existingEdition = edit.findIndex((event) => event.id === row.event_report_id);

                    return edit[existingEdition]?.edit ? (
                        <AreasDropdown edit={edit} setEdit={setEdit} row={row} />
                    ) : (
                        row.area_name
                    );
                },
            },
            {
                id: SecondaryCodesTableColumnsIdEnum.FUNCTIONAL_LOCATION,
                hideSortIcon: !Boolean(edit.findIndex((event) => event.edit)),
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.secondary_code_requirements_table.functional_location").toUpperCase()}
                    </Typography>
                ),

                render: (row) => {
                    const existingEdition = edit.findIndex((event) => event.id === row.event_report_id);

                    return edit[existingEdition]?.edit ? (
                        <FunctionalLocationsDropdown edit={edit} setEdit={setEdit} row={row} />
                    ) : (
                        row.functional_location_name
                    );
                },
            },
            {
                id: SecondaryCodesTableColumnsIdEnum.FAILURE_CODE,
                hideSortIcon: !Boolean(edit.findIndex((event) => event.edit)),
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.secondary_code_requirements_table.failure_code").toUpperCase()}
                    </Typography>
                ),

                render: (row) => {
                    const existingEdition = edit.findIndex((event) => event.id === row.event_report_id);

                    return edit[existingEdition]?.edit ? (
                        <FailureCodeDetailsDropdown
                            defaultFailureTypes={defaultFailureTypes}
                            edit={edit}
                            setEdit={setEdit}
                            row={row}
                        />
                    ) : (
                        translateDefaultEntity(
                            i18n.language,
                            row.failure_code,
                            "name",
                            DefaultTranslationFieldsInTable.FAILURE_CODE,
                        )
                    );
                },
            },
            {
                id: SecondaryCodesTableColumnsIdEnum.SECONDARY_FAILURE_CODE,
                hideSortIcon: !Boolean(edit.findIndex((event) => event.edit)),
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.secondary_code_requirements_table.secondary_failure_code").toUpperCase()}
                    </Typography>
                ),
                render: (row) => {
                    const existingEdition = edit.findIndex((event) => event.id === row.event_report_id);

                    return edit[existingEdition]?.edit ? (
                        <SecondaryFailureCodeDetailsDropdown
                            failureCodeUnknown={failureCodeUnknown}
                            edit={edit}
                            setEdit={setEdit}
                            row={row}
                        />
                    ) : row.secondary_failure_code ? (
                        translateDefaultEntity(
                            i18n.language,
                            row.secondary_failure_code as unknown as IFailureCode,
                            "name",
                            DefaultTranslationFieldsInTable.FAILURE_CODE,
                        )
                    ) : row?.failure_code?.secondary_failure_codes?.length ? (
                        <Typography className={classes.pendingMessage}>
                            {t("locations.secondary_code_requirements_table.pending")}
                        </Typography>
                    ) : (
                        t("locations.secondary_code_requirements_table.not_applicable")
                    );
                },
            },
            {
                id: SecondaryCodesTableColumnsIdEnum.EDITION_OPTIONS,
                label: "",
                hideSortIcon: true,
                render: (row) => {
                    const existingEdition = edit.findIndex((event) => event.id === row.event_report_id);
                    const editingRow = edit[existingEdition]?.edit;
                    const requiredFieldsAdded = edit[existingEdition]?.changes?.area_id
                        ? edit[existingEdition]?.changes?.functional_location_id &&
                          edit[existingEdition]?.changes?.failure_type_id &&
                          edit[existingEdition]?.changes?.failure_code_id
                        : edit[existingEdition]?.changes?.failure_type_id &&
                          edit[existingEdition]?.changes?.failure_code_id;
                    const isSaving = saving.find((savingEvent) => savingEvent.id === row.event_report_id);
                    return (
                        <List sx={{ display: "flex", alignItems: "center" }}>
                            {isSaving ? (
                                <CircularProgress size={20} color="info" />
                            ) : edit[existingEdition]?.edit ? (
                                <IconButton
                                    disabled={!requiredFieldsAdded}
                                    size="small"
                                    onClick={async () => {
                                        setSaving([{ id: row.event_report_id }]);
                                        if (existingEdition !== -1) {
                                            if (edit[existingEdition]?.changes) {
                                                const changes = {
                                                    ...edit[existingEdition]?.changes,
                                                    ...(!edit[existingEdition].location_specific && {
                                                        area_id: null,
                                                        functional_location_id: null,
                                                    }),
                                                };
                                                await updateEventReportMutation({
                                                    variables: {
                                                        eventReportId: row.event_report_id,
                                                        changes: changes,
                                                    },
                                                });
                                                await refetch();
                                                if (!showOnlyPending) {
                                                    refreshPendingEventReportsCount();
                                                }
                                            }
                                            let editExisting = [...edit];
                                            editExisting[existingEdition] = {
                                                ...editExisting[existingEdition],
                                                edit: false,
                                                changes: undefined,
                                                location_specific: row.failure_type.is_location_specific,
                                            };
                                            setEdit(editExisting);
                                        } else {
                                            setEdit((prev) => {
                                                return [
                                                    ...prev,
                                                    {
                                                        edit: false,
                                                        id: row.event_report_id,
                                                        location_specific: row.failure_type.is_location_specific,
                                                    },
                                                ];
                                            });
                                        }
                                        setSaving([]);
                                    }}
                                >
                                    <CheckIcon />
                                </IconButton>
                            ) : (
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        if (existingEdition !== -1) {
                                            let editExisting = [...edit];
                                            editExisting[existingEdition] = {
                                                ...editExisting[existingEdition],
                                                location_specific: row.failure_type.is_location_specific,
                                                edit: true,
                                                changes: {
                                                    ...(row.secondary_functional_location?.functional_location_id && {
                                                        secondary_functional_location_id:
                                                            row.secondary_functional_location?.functional_location_id,
                                                    }),

                                                    ...(row.secondary_failure_code?.failure_code_id && {
                                                        secondary_failure_code_id:
                                                            row.secondary_failure_code?.failure_code_id,
                                                    }),
                                                    ...(row.functional_location?.functional_location_id && {
                                                        functional_location_id:
                                                            row.functional_location?.functional_location_id,
                                                    }),
                                                    ...(row.area?.area_id && { area_id: row.area?.area_id }),
                                                    failure_type_id: row.failure_type.failure_type_id,
                                                    failure_code_id: row.failure_code.failure_code_id,
                                                    ...(user_id && { edited_by_user_id: user_id }),
                                                },
                                            };
                                            setEdit(editExisting);
                                        } else {
                                            setEdit((prev) => {
                                                return [
                                                    ...prev,
                                                    {
                                                        edit: true,
                                                        id: row.event_report_id,
                                                        location_specific: row.failure_type.is_location_specific,
                                                        changes: {
                                                            ...(row.secondary_functional_location
                                                                ?.functional_location_id && {
                                                                secondary_functional_location_id:
                                                                    row.secondary_functional_location
                                                                        ?.functional_location_id,
                                                            }),

                                                            ...(row.secondary_failure_code?.failure_code_id && {
                                                                secondary_failure_code_id:
                                                                    row.secondary_failure_code?.failure_code_id,
                                                            }),
                                                            ...(row.functional_location?.functional_location_id && {
                                                                functional_location_id:
                                                                    row.functional_location?.functional_location_id,
                                                            }),
                                                            ...(row.area?.area_id && { area_id: row.area?.area_id }),
                                                            failure_type_id: row.failure_type.failure_type_id,
                                                            failure_code_id: row.failure_code.failure_code_id,
                                                            ...(user_id && { edited_by_user_id: user_id }),
                                                        },
                                                    },
                                                ];
                                            });
                                        }
                                    }}
                                >
                                    <EditIcon />
                                </IconButton>
                            )}
                            <IconButton
                                size="small"
                                disabled={!editingRow}
                                onClick={() => {
                                    if (existingEdition !== -1) {
                                        let editExisting = [...edit];
                                        editExisting[existingEdition] = {
                                            ...editExisting[existingEdition],
                                            location_specific: row.failure_type.is_location_specific,
                                            edit: false,
                                            changes: undefined,
                                        };
                                        setEdit(editExisting);
                                    } else {
                                        setEdit((prev) => {
                                            return [
                                                ...prev,
                                                {
                                                    edit: false,
                                                    id: row.event_report_id,
                                                    location_specific: row.failure_type.is_location_specific,
                                                },
                                            ];
                                        });
                                    }
                                }}
                            >
                                <ClearIcon />
                            </IconButton>
                        </List>
                    );
                },
            },
        ],
        [
            classes.columnHeader,
            classes.waiting,
            classes.toBeDefined,
            classes.pendingMessage,
            t,
            millTimezone,
            userTimezone,
            updateEventReportMutation,
            user_id,
            refetch,
            i18n.language,
            saving,
            failureCodeUnknown,
            defaultFailureTypes,
            edit,
            showOnlyPending,
            refreshPendingEventReportsCount,
        ],
    );

    return columns;
};
